import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

import { ANALYTICS_QUERY_KEY } from "./useSummary";
import { IMPORT_JOBS_QUERY_KEY } from "./useImportJobs";
import { IMPORT_USAGE_QUERY_KEY } from "./useImportUsage";
import { TRADES_QUERY_KEY } from "./useTrades";

// ADR 0034: the commit now runs in the background. The endpoint returns
// 202 + { job_id }; the per-import outcome is read from the jobs poll +
// GET /imports/jobs/{job_id}/report once the job finishes.
type CommitAcceptedResponse = components["schemas"]["CommitAcceptedResponse"];

export type CommitImportVariables = {
  file: File;
  accountId: string;
  broker?: string;
};

/**
 * Thrown when the server blocks a commit with 402 because the tenant has hit
 * its monthly import allowance (ADR 0026 D3). The page branches on this to show
 * the "Upgrade — coming soon" surface instead of a generic toast.
 */
export class ImportQuotaExceededError extends Error {
  readonly used: number;
  readonly limit: number;

  constructor(used: number, limit: number) {
    super("Monthly import limit reached.");
    this.name = "ImportQuotaExceededError";
    this.used = used;
    this.limit = limit;
  }
}

function parseQuotaDetail(error: unknown): { used: number; limit: number } {
  const detail =
    typeof error === "object" && error !== null && "detail" in error
      ? (error as { detail: unknown }).detail
      : null;
  if (typeof detail === "object" && detail !== null) {
    const used = (detail as { used?: unknown }).used;
    const limit = (detail as { limit?: unknown }).limit;
    return {
      used: typeof used === "number" ? used : 0,
      limit: typeof limit === "number" ? limit : 0,
    };
  }
  return { used: 0, limit: 0 };
}

export function useCommitImport() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CommitAcceptedResponse, Error, CommitImportVariables>({
    mutationFn: async ({ file, accountId, broker }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("account_id", accountId);
      if (broker !== undefined) {
        formData.append("broker", broker);
      }
      const { data, error, response } = await client.POST("/api/imports/commit", {
        // The body shape is generated as required by openapi-typescript even
        // though FastAPI's Form() defaults make the fields optional at
        // runtime; the actual upload uses `bodySerializer: () => formData`
        // so these literals are only here to satisfy the type.
        body: {
          file,
          account_id: accountId,
          instrument_resolver_map: "{}",
          broker: broker ?? "tos",
        } as unknown as {
          file: string;
          account_id: string;
          instrument_resolver_map: string;
          broker: string;
        },
        bodySerializer: () => formData,
      });
      if (error !== undefined) {
        if (response.status === 402) {
          const { used, limit } = parseQuotaDetail(error);
          throw new ImportQuotaExceededError(used, limit);
        }
        if (response.status === 413) {
          throw new Error("File is too large. Try a smaller CSV.");
        }
        if (response.status === 400) {
          const detail =
            typeof error === "object" && error !== null && "detail" in error
              ? String((error as { detail: unknown }).detail)
              : "Import request was rejected.";
          throw new Error(detail);
        }
        throw new Error("Failed to commit import.");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TRADES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: IMPORT_USAGE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: IMPORT_JOBS_QUERY_KEY });
    },
  });
}
